import { GamesDict, SportType, SoccerCompetition } from '@/types';

export const supportedSoccerCompetitions: SoccerCompetition[] = [
  'Premier League',
  'La Liga',
  'Serie A',
  'Ligue 1',
  'Bundesliga',
  'Primeira Liga',
  'Brasileirao Serie A',
  'UEFA Champions League',
  'Copa Libertadores',
  'UEFA Euro',
  'FIFA World Cup'
];

const soccerCompetitionAliases: Record<string, SoccerCompetition> = {
  laliga: 'La Liga',
  premierleague: 'Premier League',
  brasileirao: 'Brasileirao Serie A',
  brasileiraoseriea: 'Brasileirao Serie A',
  'brasileirao serie a': 'Brasileirao Serie A',
  'brasileirao-serie-a': 'Brasileirao Serie A',
  'brazilian serie a': 'Brasileirao Serie A',
  'brazilian-serie-a': 'Brasileirao Serie A',
  'campeonato brasileiro': 'Brasileirao Serie A',
  'campeonato brasileiro serie a': 'Brasileirao Serie A',
  'campeonato brasileiro série a': 'Brasileirao Serie A',
  bsa: 'Brasileirao Serie A',
  seriea: 'Serie A',
  ligue1: 'Ligue 1',
  bundesliga: 'Bundesliga',
  primeiraliga: 'Primeira Liga',
  champions: 'UEFA Champions League',
  championsleague: 'UEFA Champions League',
  ucl: 'UEFA Champions League',
  cl: 'UEFA Champions League',
  copalibertadores: 'Copa Libertadores',
  libertadores: 'Copa Libertadores',
  cli: 'Copa Libertadores',
  uefa: 'UEFA',
  uefaeuro: 'UEFA Euro',
  euro: 'UEFA Euro',
  'fifa world cup': 'FIFA World Cup',
  'world cup': 'FIFA World Cup',
  worldcup: 'FIFA World Cup',
  'world-cup': 'FIFA World Cup',
  fifaworldcup: 'FIFA World Cup',
  'fifa-world-cup': 'FIFA World Cup',
  'fifa world cup 2026': 'FIFA World Cup',
  'world cup 2026': 'FIFA World Cup',
  worldcup2026: 'FIFA World Cup',
  'world-cup-2026': 'FIFA World Cup',
  wc26: 'FIFA World Cup',
  fifaworldcup2026: 'FIFA World Cup',
  'fifa-world-cup-2026': 'FIFA World Cup'
};

const soccerCompetitionQueryValues: Record<string, string> = {
  'La Liga': 'laliga',
  'Premier League': 'premierleague',
  'Serie A': 'seriea',
  'Ligue 1': 'ligue1',
  'Bundesliga': 'bundesliga',
  'Primeira Liga': 'primeiraliga',
  'Brasileirao Serie A': 'brasileirao',
  'Champions League': 'championsleague',
  'UEFA Champions League': 'championsleague',
  'Copa Libertadores': 'copalibertadores',
  'UEFA': 'uefa',
  'UEFA Euro': 'uefaeuro',
  'FIFA World Cup': 'fifa-world-cup',
  'FIFA World Cup 2026': 'fifa-world-cup',
  'All': 'all'
};

const knownCompetitionOrder = new Map(
  supportedSoccerCompetitions.map((competition, index) => [competition, index])
);

const uniqueCompetitionNames = (competitions: SoccerCompetition[]): SoccerCompetition[] => {
  const seen = new Set<SoccerCompetition>();
  const unique: SoccerCompetition[] = [];

  for (const competition of competitions) {
    if (!competition || competition === 'All' || seen.has(competition)) {
      continue;
    }

    seen.add(competition);
    unique.push(competition);
  }

  return unique;
};

export const sortSoccerCompetitions = (
  competitions: SoccerCompetition[]
): SoccerCompetition[] =>
  uniqueCompetitionNames(competitions).sort((left, right) => {
    const leftOrder = knownCompetitionOrder.get(left);
    const rightOrder = knownCompetitionOrder.get(right);

    if (leftOrder !== undefined && rightOrder !== undefined) {
      return leftOrder - rightOrder;
    }

    if (leftOrder !== undefined) {
      return -1;
    }

    if (rightOrder !== undefined) {
      return 1;
    }

    return left.localeCompare(right);
  });

export const getAvailableSoccerCompetitions = (
  soccerGames?: GamesDict[string]
): SoccerCompetition[] => {
  if (!soccerGames) return [];

  return sortSoccerCompetitions(
    Object.values(soccerGames)
      .map(game => game.competition)
      .filter((competition): competition is SoccerCompetition => Boolean(competition))
  );
};

// Sport descriptions
export const sportDescriptions: Record<SportType, string> = {
  'nba': 'Choose completed NBA games with spoiler-safe point-margin filters.',
  'soccer': 'Choose completed soccer matches by total goals and competition before score-first apps catch you.',
  'nfl': 'Choose completed NFL games with spoiler-safe point-margin filters.',
  'mlb': 'Choose completed MLB games by run margin before the result finds you.',
  'nhl': 'Choose completed NHL games by total goals without revealing the winner.'
};

// Get display name for sport
export const getSportDisplayName = (sport: SportType): string => {
  switch (sport) {
    case 'nba': return 'NBA';
    case 'nfl': return 'NFL';
    case 'mlb': return 'MLB';
    case 'soccer': return 'Soccer';
    case 'nhl': return 'NHL';
    default: return sport as string;
  }
}; 

// Map URL path param to actual competition name
export const mapUrlParamToCompetition = (param?: string): SoccerCompetition => {
  if (!param) return 'All';

  const normalized = param.trim();
  if (!normalized) return 'All';

  if (normalized.toLowerCase() === 'all') {
    return 'All';
  }

  const knownCompetition = supportedSoccerCompetitions.find(
    competition => competition.toLowerCase() === normalized.toLowerCase()
  );

  if (knownCompetition) {
    return knownCompetition;
  }

  return soccerCompetitionAliases[normalized.toLowerCase()] || normalized;
};

// Helper function to get URL slug for a competition
export const getCompetitionUrlSlug = (comp: SoccerCompetition): string => {
  if (!comp || comp === 'All') return '';

  return soccerCompetitionQueryValues[comp] || comp;
};

// Get a formatted list of selected competitions for display
export const formatSelectedCompetitions = (competitions: SoccerCompetition[]): string => {
  if (!competitions || competitions.length === 0) {
    return 'All Competitions';
  }
  
  if (competitions.length === 1) {
    return competitions[0];
  }
  
  if (competitions.length === 2) {
    return `${competitions[0]} and ${competitions[1]}`;
  }
  
  return `${competitions.length} Competitions`;
};

// Function to get display name for a competition slug
export const getCompetitionDisplayName = (sport: SportType, competitionSlug: string): string => {
  // For non-soccer sports, the slug might be the display name or need specific mapping if they have sub-competitions
  if (sport !== 'soccer') {
    // Example: if NFL has 'playoffs' or 'superbowl' slugs
    if (sport === 'nfl') {
      if (competitionSlug.toLowerCase() === 'superbowl') return 'Super Bowl';
      if (competitionSlug.toLowerCase() === 'playoffs') return 'NFL Playoffs';
    }
    // For NBA
    if (sport === 'nba') {
      if (competitionSlug.toLowerCase() === 'playoffs') return 'NBA Playoffs';
      if (competitionSlug.toLowerCase() === 'finals') return 'NBA Finals';
    }
    // For MLB
    if (sport === 'mlb') {
      if (competitionSlug.toLowerCase() === 'worldseries') return 'World Series';
      if (competitionSlug.toLowerCase() === 'playoffs') return 'MLB Playoffs';
    }
    // For NHL
    if (sport === 'nhl') {
      if (competitionSlug.toLowerCase() === 'stanleycup') return 'Stanley Cup Finals';
      if (competitionSlug.toLowerCase() === 'playoffs') return 'NHL Playoffs';
    }
    // Default: capitalize the slug or return as is
    return competitionSlug.charAt(0).toUpperCase() + competitionSlug.slice(1);
  }

  return mapUrlParamToCompetition(competitionSlug);
};
